'use client';

import { Badge } from '@/components/catalyst/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/shadcn/dropdown-menu';
import { useToast } from '@/components/ui/Toasts/use-toast';
import { getInterviewStatusLabel } from '@/types/meta';
import { InterviewStatus } from '@/types/pulse';
import { updateInterviewStatus } from '@/utils/supabase/actions';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

function getBadgeContent(status: InterviewStatus) {
  const label = getInterviewStatusLabel(status);
  switch (status) {
    case InterviewStatus.DRAFT:
      return { color: 'zinc' as const, label };
    case InterviewStatus.LIVE:
      return { color: 'green' as const, label };
    case InterviewStatus.COMPLETE:
      return { color: 'blue' as const, label };
    default:
      return null;
  }
}

export default function InterviewBadge({
  status,
  className,
  editable = false,
  interviewId,
  cntConversations
}: {
  status: InterviewStatus;
  className?: string;
  editable?: boolean;
  interviewId?: number;
  cntConversations?: number;
}) {
  const [isUpdating, setIsUpdating] = useState(false);
  const { toast } = useToast();
  const router = useRouter();

  const content = getBadgeContent(status);
  if (!content) return null;

  const badge = (
    <Badge color={content.color} className={className}>
      {content.label}
    </Badge>
  );

  if (!editable || !interviewId) {
    return badge;
  }

  const handleStatusChange = async (newStatus: InterviewStatus) => {
    setIsUpdating(true);
    try {
      await updateInterviewStatus(interviewId, newStatus);
      toast({
        title: 'Status updated',
        description: `Interview status changed to ${getInterviewStatusLabel(newStatus)}`
      });
      router.refresh();
    } catch (error) {
      toast({
        title: 'Failed to update status',
        description: 'Please try again',
        variant: 'destructive'
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className={isUpdating ? 'opacity-50' : 'cursor-pointer'}>
          {badge}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {status === InterviewStatus.DRAFT && (
          <DropdownMenuItem
            disabled={isUpdating}
            onClick={() => handleStatusChange(InterviewStatus.LIVE)}
          >
            Go Live
          </DropdownMenuItem>
        )}
        {status === InterviewStatus.LIVE && (cntConversations ?? 0) > 0 && (
          <DropdownMenuItem
            disabled={isUpdating}
            onClick={() => handleStatusChange(InterviewStatus.COMPLETE)}
          >
            Close Interview
          </DropdownMenuItem>
        )}
        {status === InterviewStatus.LIVE && cntConversations === 0 && (
          <DropdownMenuItem
            disabled={isUpdating}
            onClick={() => handleStatusChange(InterviewStatus.DRAFT)}
          >
            Reset to Draft
          </DropdownMenuItem>
        )}
        {status === InterviewStatus.COMPLETE && (
          <DropdownMenuItem
            disabled={isUpdating}
            onClick={() => handleStatusChange(InterviewStatus.LIVE)}
          >
            Set to Live
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
